import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ImageUploadField from "../components/ImageUploadField";
import { ScaledVideoFrame } from "../components/ScaledVideoFrame";
import {
  BookOpen,
  Play,
  Plus,
  Pencil,
  Trash2,
  X,
  Search as SearchIcon,
  FolderOpen,
  Layers,
  Loader2,
  AlertTriangle,
  Star,
  CheckCircle2,
  RotateCcw,
  ExternalLink,
} from "lucide-react";

/* ---------------- Lesson Viewer Modal (lazy iframe) ---------------- */

function LessonModal({ lesson, onClose, onToggleFavorite, onComplete, onReset }) {
  // Only mount the iframe when the modal is open — guarantees we never
  // preload Google Drive players for lessons the user did not click.
  if (!lesson) return null;
  const isFav = !!lesson.favorite;
  const isDone = !!lesson.completed || lesson.progress >= 100;
  return (
    <div
      data-testid="lesson-modal"
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      {/* Mobile: full-screen player sheet; desktop: centered card. */}
      <div
        className="bg-[#0F1218] sm:border border-[#282D3D] sm:rounded-2xl w-full max-w-4xl overflow-y-auto sm:overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-[#282D3D]">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-extrabold text-white leading-tight">
              {lesson.title}
            </h2>
            {lesson.description && (
              <p className="text-xs text-gray-400 mt-1 line-clamp-2">{lesson.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onToggleFavorite && (
              <button
                type="button"
                data-testid="lesson-modal-favorite"
                onClick={() => onToggleFavorite(lesson)}
                aria-pressed={isFav}
                title={isFav ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                className={`p-1.5 rounded-lg transition-colors ${
                  isFav
                    ? "text-yellow-300 hover:text-yellow-200 bg-yellow-500/10"
                    : "text-gray-400 hover:text-yellow-300 hover:bg-[#1A1D26]"
                }`}
              >
                <Star className={`w-5 h-5 ${isFav ? "fill-yellow-300" : ""}`} />
              </button>
            )}
            <button
              data-testid="lesson-modal-close"
              onClick={onClose}
              className="shrink-0 p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-[#1A1D26]"
              aria-label="Fechar aula"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 sm:flex-none flex items-center bg-black">
          <ScaledVideoFrame
            key={lesson.id}
            testId="lesson-iframe"
            src={lesson.embed_url || lesson.video_url}
            title={lesson.title}
          />
        </div>
        {lesson.video_url && (
          <a
            href={lesson.video_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lesson-open-external"
            className="sm:hidden flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-300 bg-[#14161C] border-t border-[#282D3D] py-2.5 hover:text-white"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir em tela cheia (Google Drive)
          </a>
        )}
        <div className="px-4 py-3 border-t border-[#282D3D] flex flex-wrap items-center gap-2">
          {lesson.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mr-auto">
              {lesson.tags.map((t) => (
                <span
                  key={t}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1A1D26] border border-[#3E4760] text-gray-300"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {isDone ? (
              <>
                <span
                  data-testid="lesson-modal-completed-badge"
                  className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-2.5 py-1 rounded-full"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Concluída
                </span>
                {onReset && (
                  <button
                    type="button"
                    data-testid="lesson-modal-reset"
                    onClick={() => onReset(lesson)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-300 bg-[#1A1D26] border border-[#3E4760] hover:border-[#FF6B00] px-2.5 py-1 rounded-full"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reiniciar
                  </button>
                )}
              </>
            ) : (
              onComplete && (
                <button
                  type="button"
                  data-testid="lesson-modal-complete"
                  onClick={() => onComplete(lesson)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-black bg-emerald-400 hover:bg-emerald-300 px-3 py-1.5 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Marcar como concluída
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Lesson Form Modal ---------------- */

function LessonFormModal({ open, lesson, sections, categories, defaultSectionId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [order, setOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (lesson) {
      setTitle(lesson.title || "");
      setVideoUrl(lesson.video_url || "");
      setDescription(lesson.description || "");
      setThumbnail(lesson.thumbnail_url || "");
      setSectionId(lesson.section_id || defaultSectionId || "");
      setTagsRaw((lesson.tags || []).join(", "));
      setOrder(lesson.order || 0);
    } else {
      setTitle("");
      setVideoUrl("");
      setDescription("");
      setThumbnail("");
      setSectionId(defaultSectionId || "");
      setTagsRaw("");
      setOrder(0);
    }
  }, [open, lesson, defaultSectionId]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !sectionId || !videoUrl.trim()) {
      setError("Preencha título, seção e URL do vídeo.");
      return;
    }
    const payload = {
      title: title.trim(),
      section_id: sectionId,
      video_url: videoUrl.trim(),
      description: description.trim() || undefined,
      thumbnail_url: thumbnail || undefined,
      tags: tagsRaw
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
      order: Number(order) || 0,
    };
    setSaving(true);
    try {
      if (lesson) {
        await api.patch(`/api/library/lessons/${lesson.id}`, payload);
      } else {
        await api.post("/api/library/lessons", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar aula.");
    } finally {
      setSaving(false);
    }
  };

  const catName = (secId) => {
    const sec = sections.find((s) => s.id === secId);
    if (!sec) return "";
    const cat = categories.find((c) => c.id === sec.category_id);
    return cat ? `${cat.name} / ${sec.name}` : sec.name;
  };

  return (
    <div
      data-testid="lesson-form-modal"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-xl p-5 sm:p-6 space-y-3 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-extrabold text-white">
            {lesson ? "Editar aula" : "Nova aula"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="lesson-form-close"
            className="p-1 text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">Título *</label>
          <input
            data-testid="lesson-form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
          />
        </div>

        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">Seção *</label>
          <select
            data-testid="lesson-form-section"
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            required
            className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
          >
            <option value="">— selecione —</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {catName(s.id)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">
            URL do Google Drive *
          </label>
          <input
            data-testid="lesson-form-video-url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://drive.google.com/file/d/FILE_ID/view"
            required
            className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            Compartilhe o arquivo como "Qualquer pessoa com o link" para que o aluno consiga assistir.
          </p>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">Descrição</label>
          <textarea
            data-testid="lesson-form-description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
          />
        </div>

        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">Thumbnail</label>
          <ImageUploadField
            value={thumbnail}
            onChange={setThumbnail}
            testId="lesson-form-thumb"
            placeholder="Cole a URL ou envie a capa"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Tags</label>
            <input
              data-testid="lesson-form-tags"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="ex: live, pre-teste, criativo"
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Ordem</label>
            <input
              type="number"
              data-testid="lesson-form-order"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="lesson-form-error"
            className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-bold text-gray-300 bg-[#1F2330] border border-[#3E4760]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="lesson-form-submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-wider bg-[#FF6B00] hover:bg-[#FF8533] text-black disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Salvar
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Simple name prompt (Category / Section) ---------------- */

function NamePromptModal({ open, title, label, initial = "", onClose, onSave }) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValue(initial);
      setError("");
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) {
      setError("Informe o nome.");
      return;
    }
    setSaving(true);
    try {
      await onSave(value.trim());
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="name-prompt-modal"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-sm p-5 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-extrabold text-white">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-300 mb-1">{label}</label>
          <input
            data-testid="name-prompt-input"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
          />
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-gray-300 bg-[#1F2330] border border-[#3E4760]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="name-prompt-save"
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider bg-[#FF6B00] text-black disabled:opacity-60"
          >
            Salvar
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Lesson Card ---------------- */

function LessonCard({ lesson, canEdit, onPlay, onEdit, onDelete, onToggleFavorite }) {
  const isFav = !!lesson.favorite;
  const progress = Math.max(0, Math.min(100, Number(lesson.progress || 0)));
  const isDone = !!lesson.completed || progress >= 100;
  return (
    <div
      data-testid={`lesson-card-${lesson.id}`}
      className="bg-[#14161C] border border-[#282D3D] rounded-xl overflow-hidden flex flex-col hover:border-[#3E4760] transition-colors"
    >
      <button
        type="button"
        data-testid={`lesson-watch-${lesson.id}`}
        onClick={() => onPlay(lesson)}
        className="relative block w-full bg-[#0B0D12] group"
        style={{ aspectRatio: "16 / 9" }}
      >
        {lesson.thumbnail_url ? (
          <img
            src={lesson.thumbnail_url}
            alt={lesson.title}
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1A1D26] to-[#0B0D12]">
            <BookOpen className="w-10 h-10 text-[#3E4760]" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF6B00] text-black font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_15px_rgba(255,107,0,0.45)]">
            <Play className="w-3.5 h-3.5 fill-black" /> Assistir aula
          </span>
        </div>

        {/* Favorite toggle (top-right overlay) */}
        {onToggleFavorite && (
          <span
            role="button"
            tabIndex={0}
            data-testid={`lesson-favorite-${lesson.id}`}
            aria-pressed={isFav}
            aria-label={isFav ? "Remover dos favoritos" : "Favoritar aula"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(lesson);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite(lesson);
              }
            }}
            className={`absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer ${
              isFav
                ? "bg-yellow-500/90 text-black"
                : "bg-black/60 text-white hover:bg-black/80"
            }`}
          >
            <Star className={`w-4 h-4 ${isFav ? "fill-black" : ""}`} />
          </span>
        )}

        {/* Completion badge (top-left overlay) */}
        {isDone && (
          <span
            data-testid={`lesson-completed-${lesson.id}`}
            className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-black text-[9px] font-extrabold uppercase tracking-wider"
          >
            <CheckCircle2 className="w-3 h-3" /> Concluída
          </span>
        )}

        {/* Progress bar (bottom overlay) */}
        {progress > 0 && !isDone && (
          <div
            data-testid={`lesson-progress-${lesson.id}`}
            className="absolute bottom-0 left-0 right-0 h-1 bg-black/40"
          >
            <div
              className="h-full bg-[#FF6B00]"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </button>
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <h4 className="text-sm font-extrabold text-white leading-snug line-clamp-2">
          {lesson.title}
        </h4>
        {lesson.description && (
          <p className="text-[11px] text-gray-400 line-clamp-2">{lesson.description}</p>
        )}
        {lesson.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto pt-1">
            {lesson.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1A1D26] border border-[#282D3D] text-gray-400"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="flex items-center gap-1.5 pt-2 mt-auto border-t border-[#232733]">
            <button
              type="button"
              data-testid={`lesson-edit-${lesson.id}`}
              onClick={() => onEdit(lesson)}
              className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#1A1D26] border border-[#3E4760] text-gray-200 hover:border-[#FF6B00]"
            >
              <Pencil className="w-3 h-3" /> Editar
            </button>
            <button
              type="button"
              data-testid={`lesson-delete-${lesson.id}`}
              onClick={() => onDelete(lesson)}
              className="inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Main page ---------------- */

export default function LibraryPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [tree, setTree] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [playingLesson, setPlayingLesson] = useState(null);

  // Form modals
  const [catPrompt, setCatPrompt] = useState({ open: false, initial: "", cat: null });
  const [secPrompt, setSecPrompt] = useState({ open: false, initial: "", sec: null, catId: null });
  const [lessonForm, setLessonForm] = useState({ open: false, lesson: null, sectionId: null });

  const fetchTree = useCallback(async () => {
    setErrorMsg("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      const res = await api.get("/api/library/tree", { params });
      setTree(res.data.categories || []);
      setCanEdit(!!res.data.can_edit);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Não foi possível carregar a biblioteca.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (user) fetchTree();
  }, [user, fetchTree]);

  /* ---------- Favourites + progress ---------- */

  const applyLessonPatch = useCallback((lessonId, patch) => {
    setTree((prev) =>
      prev.map((c) => ({
        ...c,
        sections: (c.sections || []).map((s) => ({
          ...s,
          lessons: (s.lessons || []).map((l) =>
            l.id === lessonId ? { ...l, ...patch } : l
          ),
        })),
      }))
    );
    setPlayingLesson((prev) =>
      prev && prev.id === lessonId ? { ...prev, ...patch } : prev
    );
  }, []);

  const toggleFavorite = useCallback(
    async (lesson) => {
      // Optimistic flip
      applyLessonPatch(lesson.id, { favorite: !lesson.favorite });
      try {
        const res = await api.post(`/api/library/lessons/${lesson.id}/favorite`);
        applyLessonPatch(lesson.id, { favorite: !!res.data.favorite });
      } catch (err) {
        // Revert on failure
        applyLessonPatch(lesson.id, { favorite: lesson.favorite });
      }
    },
    [applyLessonPatch]
  );

  const markStarted = useCallback(
    async (lesson) => {
      // Avoid round-trip if we've already recorded progress.
      if ((lesson.progress || 0) >= 15 || lesson.completed) return;
      try {
        const res = await api.post(`/api/library/lessons/${lesson.id}/progress`, {
          status: "started",
        });
        const l = res.data?.lesson || {};
        applyLessonPatch(lesson.id, {
          progress: l.progress ?? 15,
          completed: !!l.completed,
          last_watched_at: l.last_watched_at,
        });
      } catch (_) {
        /* non-fatal */
      }
    },
    [applyLessonPatch]
  );

  const markCompleted = useCallback(
    async (lesson) => {
      applyLessonPatch(lesson.id, { progress: 100, completed: true });
      try {
        const res = await api.post(`/api/library/lessons/${lesson.id}/progress`, {
          status: "completed",
        });
        const l = res.data?.lesson || {};
        applyLessonPatch(lesson.id, {
          progress: l.progress ?? 100,
          completed: !!l.completed,
          last_watched_at: l.last_watched_at,
        });
      } catch (err) {
        applyLessonPatch(lesson.id, {
          progress: lesson.progress || 0,
          completed: !!lesson.completed,
        });
      }
    },
    [applyLessonPatch]
  );

  const resetProgress = useCallback(
    async (lesson) => {
      applyLessonPatch(lesson.id, { progress: 0, completed: false });
      try {
        await api.post(`/api/library/lessons/${lesson.id}/progress`, { status: "reset" });
      } catch (_) {
        applyLessonPatch(lesson.id, {
          progress: lesson.progress || 0,
          completed: !!lesson.completed,
        });
      }
    },
    [applyLessonPatch]
  );

  const openLesson = useCallback(
    (lesson) => {
      setPlayingLesson(lesson);
      markStarted(lesson);
    },
    [markStarted]
  );

  // Deep link: /biblioteca?lesson=<id> opens the viewer directly (used by
  // the Counselor CTA on the Home page).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get("lesson");
    if (!lessonId || loading) return;
    const found = tree
      .flatMap((c) => c.sections || [])
      .flatMap((s) => s.lessons || [])
      .find((l) => l.id === lessonId);
    if (found) {
      setPlayingLesson(found);
      markStarted(found);
    } else {
      // Fallback — fetch the lesson directly (student may have search
      // filters applied that hide it in the tree).
      api
        .get(`/api/library/lessons/${lessonId}`)
        .then((res) => {
          setPlayingLesson(res.data.lesson);
          markStarted(res.data.lesson);
        })
        .catch(() => {});
    }
    // run only on initial mount after tree is ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tree, location.search]);

  const flatSections = useMemo(
    () => tree.flatMap((c) => (c.sections || []).map((s) => ({ ...s, category_name: c.name }))),
    [tree]
  );
  const flatCategories = useMemo(
    () => tree.map((c) => ({ id: c.id, name: c.name, order: c.order })),
    [tree]
  );

  /* ---------- CRUD helpers ---------- */

  const saveCategory = async (name) => {
    if (catPrompt.cat) {
      await api.patch(`/api/library/categories/${catPrompt.cat.id}`, { name });
    } else {
      await api.post("/api/library/categories", { name });
    }
    await fetchTree();
  };

  const saveSection = async (name) => {
    if (secPrompt.sec) {
      await api.patch(`/api/library/sections/${secPrompt.sec.id}`, { name });
    } else {
      await api.post("/api/library/sections", {
        name,
        category_id: secPrompt.catId,
      });
    }
    await fetchTree();
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`Arquivar a categoria "${c.name}" e todas as suas seções/aulas?`)) return;
    await api.delete(`/api/library/categories/${c.id}`);
    await fetchTree();
  };
  const deleteSection = async (s) => {
    if (!window.confirm(`Arquivar a seção "${s.name}" e todas as suas aulas?`)) return;
    await api.delete(`/api/library/sections/${s.id}`);
    await fetchTree();
  };
  const deleteLesson = async (l) => {
    if (!window.confirm(`Arquivar a aula "${l.title}"?`)) return;
    await api.delete(`/api/library/lessons/${l.id}`);
    await fetchTree();
  };

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <div
        data-testid="library-page-container"
        className="p-8 text-center text-gray-400"
      >
        Carregando biblioteca...
      </div>
    );
  }

  return (
    <div
      data-testid="library-page-container"
      className="space-y-5 max-w-5xl mx-auto px-4 py-4 pb-20"
    >
      {/* Header */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
            <BookOpen className="w-4 h-4" />
            <span>Biblioteca da Caverna</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-white">Aulas gravadas</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Materiais curados pela mentoria para você revisar antes e durante cada ciclo Live.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            data-testid="library-add-category-btn"
            onClick={() => setCatPrompt({ open: true, initial: "", cat: null })}
            className="self-start sm:self-auto px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(255,107,0,0.35)] inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5 stroke-[3]" />
            Nova categoria
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <SearchIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="library-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por título, descrição ou tag"
          className="w-full bg-[#14161C] border border-[#282D3D] rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
        />
      </div>

      {errorMsg && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {errorMsg}
        </div>
      )}

      {tree.length === 0 && !errorMsg && (
        <div
          data-testid="library-empty"
          className="bg-[#14161C] border border-dashed border-[#282D3D] rounded-xl p-10 text-center text-gray-400 text-sm"
        >
          {canEdit
            ? "Nenhuma categoria ainda. Comece criando uma categoria para organizar as aulas."
            : "O mentor ainda não publicou aulas. Volte em breve."}
        </div>
      )}

      {tree.map((cat) => (
        <section
          key={cat.id}
          data-testid={`library-category-${cat.id}`}
          className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#FF6B00]" />
              <h2 className="text-base font-extrabold text-white">{cat.name}</h2>
              <span className="text-[10px] text-gray-500">
                {(cat.sections || []).reduce((a, s) => a + (s.lessons?.length || 0), 0)} aulas
              </span>
            </div>
            {canEdit && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid={`category-add-section-${cat.id}`}
                  onClick={() => setSecPrompt({ open: true, initial: "", sec: null, catId: cat.id })}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#1F2330] border border-[#3E4760] text-gray-200 hover:border-[#FF6B00]"
                >
                  <Plus className="w-3 h-3" /> Seção
                </button>
                <button
                  type="button"
                  data-testid={`category-edit-${cat.id}`}
                  onClick={() => setCatPrompt({ open: true, initial: cat.name, cat })}
                  className="p-1 rounded text-gray-400 hover:text-white hover:bg-[#1A1D26]"
                  title="Renomear"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  data-testid={`category-delete-${cat.id}`}
                  onClick={() => deleteCategory(cat)}
                  className="p-1 rounded text-red-300 hover:text-red-200 hover:bg-red-500/10"
                  title="Arquivar categoria"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {(cat.sections || []).length === 0 && (
            <div className="text-[11px] text-gray-500 italic">
              {canEdit ? "Nenhuma seção. Clique em “+ Seção” para começar." : "Sem conteúdo nesta categoria."}
            </div>
          )}

          {(cat.sections || []).map((sec) => (
            <div
              key={sec.id}
              data-testid={`library-section-${sec.id}`}
              className="rounded-lg bg-[#101319] border border-[#232733] p-3 space-y-3"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-gray-400" />
                  <h3 className="text-sm font-bold text-white">{sec.name}</h3>
                  <span className="text-[10px] text-gray-500">
                    {(sec.lessons || []).length} aulas
                  </span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid={`section-add-lesson-${sec.id}`}
                      onClick={() =>
                        setLessonForm({ open: true, lesson: null, sectionId: sec.id })
                      }
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FF6B00]/15 border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/25"
                    >
                      <Plus className="w-3 h-3" /> Aula
                    </button>
                    <button
                      type="button"
                      data-testid={`section-edit-${sec.id}`}
                      onClick={() => setSecPrompt({ open: true, initial: sec.name, sec, catId: cat.id })}
                      className="p-1 rounded text-gray-400 hover:text-white hover:bg-[#1A1D26]"
                      title="Renomear"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`section-delete-${sec.id}`}
                      onClick={() => deleteSection(sec)}
                      className="p-1 rounded text-red-300 hover:text-red-200 hover:bg-red-500/10"
                      title="Arquivar seção"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {(sec.lessons || []).length === 0 ? (
                <div className="text-[11px] text-gray-500 italic">
                  {canEdit ? "Nenhuma aula nessa seção." : "Em breve."}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(sec.lessons || []).map((l) => (
                    <LessonCard
                      key={l.id}
                      lesson={l}
                      canEdit={canEdit}
                      onPlay={openLesson}
                      onEdit={(lesson) =>
                        setLessonForm({ open: true, lesson, sectionId: sec.id })
                      }
                      onDelete={deleteLesson}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>
      ))}

      {/* Modals */}
      <LessonModal
        lesson={playingLesson}
        onClose={() => setPlayingLesson(null)}
        onToggleFavorite={toggleFavorite}
        onComplete={markCompleted}
        onReset={resetProgress}
      />
      <LessonFormModal
        open={lessonForm.open}
        lesson={lessonForm.lesson}
        sections={flatSections}
        categories={flatCategories}
        defaultSectionId={lessonForm.sectionId}
        onClose={() => setLessonForm({ open: false, lesson: null, sectionId: null })}
        onSaved={fetchTree}
      />
      <NamePromptModal
        open={catPrompt.open}
        title={catPrompt.cat ? "Renomear categoria" : "Nova categoria"}
        label="Nome da categoria"
        initial={catPrompt.initial}
        onClose={() => setCatPrompt({ open: false, initial: "", cat: null })}
        onSave={saveCategory}
      />
      <NamePromptModal
        open={secPrompt.open}
        title={secPrompt.sec ? "Renomear seção" : "Nova seção"}
        label="Nome da seção"
        initial={secPrompt.initial}
        onClose={() => setSecPrompt({ open: false, initial: "", sec: null, catId: null })}
        onSave={saveSection}
      />
    </div>
  );
}
